import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl';

import { useSelectMessages } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  Form,
  FormItem,
  FormRow,
  Input,
  SecondaryBtn,
  Select,
  SuccessAlert,
  WaitingAlert,
} from '@tmapy/style-guide';

import { FormBlock } from '../../app/components/FormBlock';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';
import { getFormValues } from '../../docs/utils/getformvalues';
import { useHistoryAction, useInput } from '../../hooks';
import type { InputProps } from '../../hooks/useInput';
import type { UseResource } from '../../hooks/useResource';
import type {
  PriceListItemRequest,
  PriceListItemResponse,
} from '../../openapi/types/PriceListItem';
import type { PriceBandResponse } from '../../openapi/types/PriceBand';
import { priceListItemsList } from '../../openapi/operations/priceListItemsList';
import { PriceListItemTypesEnum } from '../../openapi/types/PriceListItemTypesEnum';
import { PriceListItemUnitsEnum } from '../../openapi/types/PriceListItemUnitsEnum';

import { PRICE_LIST_ITEM_FORM_TYPE } from '../constants';
import { isDataPriceListItemType } from '../utils';
import { PriceBandsTable } from './PriceBandsTable';
import {
  priceListMessages,
  priceListItemTypeMessages,
  priceListItemUnitMessages,
  priceListFormMessages,
  priceListFormBtnMessages,
  priceListFormFailMessages,
  priceListFormQueryMessages,
  priceListFormTitleMessages,
} from './priceListMessages';

type PriceListItemFormProps = {
  formType: PRICE_LIST_ITEM_FORM_TYPE;
  resource: UseResource<PriceListItemResponse>;
  submitQueryState: QUERY_STATE;
  deleteQueryState: QUERY_STATE;
  /** Mazání zablokoval backend kvůli navázané výdejní sadě, ne kvůli chybě requestu. */
  isDeleteBlockedByUse?: boolean;
  onSubmit(value: PriceListItemRequest): void;
  onDelete(): void;
};

export const PriceListItemForm: React.FC<PriceListItemFormProps> = ({
  formType,
  resource,
  submitQueryState,
  deleteQueryState,
  isDeleteBlockedByUse,
  onSubmit,
  onDelete,
}) => {
  const intl = useIntl();
  const loadQueryState = resource.queryState;
  const isFormValid = useRef(true);

  const [validate, setValidate] = useState(false);
  const [isFormChanged, setFormChanged] = useState(false);

  const setFormInvalid = (message = 'VALUE_IS_NOT_VALID'): string => {
    isFormValid.current = false;
    return message;
  };

  const onInputChangeCallback = useCallback(() => {
    setFormChanged(true);
    setValidate(false);
  }, [setFormChanged, setValidate]);

  // INPUTS
  const name = useInput<InputProps>({
    name: 'name',
    label: intl.formatMessage(priceListMessages.name),
    initialValue: resource.data?.name ?? '',
    isRequired: true,
    autoFocus: formType === PRICE_LIST_ITEM_FORM_TYPE.NEW,
    onChange: onInputChangeCallback,
    validate,
    validator: (value) =>
      value.trim()
        ? null
        : setFormInvalid(intl.formatMessage(priceListFormMessages.nameErrorMessage)),
  });

  const typeItems = useSelectMessages(priceListItemTypeMessages, PriceListItemTypesEnum);
  const type = useInput({
    name: 'type',
    label: intl.formatMessage(priceListMessages.type),
    initialValue: (resource.data?.type as string) ?? '',
    items: typeItems,
    isRequired: true,
    onChange: onInputChangeCallback,
    validate,
    validator: (value) =>
      value ? null : setFormInvalid(intl.formatMessage(priceListFormMessages.typeErrorMessage)),
  });

  /**
   * U datových typů je cena daná pásmy, která se plní v administraci — jednotka
   * ani cena za jednotku se proto nezadávají, nevalidují a neodesílají.
   */
  const isDataType = isDataPriceListItemType(type.value as PriceListItemTypesEnum);

  /**
   * Pásma spravuje výhradně server a k nové položce se doplní až po uložení.
   * Aby bylo hned po výběru typu vidět, podle čeho se bude účtovat, přebíráme je
   * z existující položky téhož typu — v tabulce je to označené jako pásma typu.
   */
  const ownBands = resource.data?.bands;
  const [typeBands, setTypeBands] = useState<PriceBandResponse[] | null>(null);

  useEffect(() => {
    if (!isDataType || ownBands?.length) {
      setTypeBands(null);
      return;
    }

    let isStale = false;
    void (async () => {
      const response = await priceListItemsList({
        query: { type: type.value as PriceListItemTypesEnum },
      });
      if (isStale) return;

      const itemWithBands = response.ok
        ? response.data?.results?.find((item) => item.bands?.length)
        : undefined;
      setTypeBands(itemWithBands?.bands ?? null);
    })();

    return () => {
      isStale = true;
    };
  }, [isDataType, type.value, ownBands]);

  const unitItems = useSelectMessages(priceListItemUnitMessages, PriceListItemUnitsEnum);
  const unit = useInput({
    name: 'unit',
    label: intl.formatMessage(priceListMessages.unit),
    initialValue: (resource.data?.unit as string) ?? '',
    items: unitItems,
    isRequired: !isDataType,
    onChange: onInputChangeCallback,
    validate,
    validator: (value) =>
      isDataType || value
        ? null
        : setFormInvalid(intl.formatMessage(priceListFormMessages.unitErrorMessage)),
  });

  const unit_price = useInput<InputProps>({
    name: 'unit_price',
    label: intl.formatMessage(priceListMessages.unitPrice),
    initialValue: resource.data?.unit_price ?? '',
    isRequired: !isDataType,
    onChange: onInputChangeCallback,
    validate,
    validator: (value) =>
      isDataType || (value !== '' && !Number.isNaN(Number(value)) && Number(value) >= 0)
        ? null
        : setFormInvalid(intl.formatMessage(priceListFormMessages.unitPriceErrorMessage)),
  });

  const vat_rate = useInput<InputProps>({
    name: 'vat_rate',
    label: intl.formatMessage(priceListMessages.vatRate),
    initialValue: resource.data?.vat_rate ?? '',
    isRequired: true,
    onChange: onInputChangeCallback,
    validate,
    validator: (value) =>
      value !== '' && !Number.isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100
        ? null
        : setFormInvalid(intl.formatMessage(priceListFormMessages.vatRateErrorMessage)),
  });

  // POST
  const formItems = {
    name,
    type,
    unit,
    unit_price,
    vat_rate,
  };

  // HANDLERS
  const handleClose = useHistoryAction('vydejPriceList');
  const handleClear = useCallback(() => resource.refetch(), [resource]);
  const handleCancel = useCallback(() => resource.refetch(), [resource]);

  const handleDelete = async () => {
    if (window.confirm(intl.formatMessage(priceListFormMessages.priceListItemAlertDelete))) {
      onDelete();
    }
  };

  const handleSubmit = useCallback(() => {
    isFormValid.current = true;
    setValidate(true);
  }, [setValidate]);

  const submit = async () => {
    setValidate(false);
    const values = getFormValues(formItems);
    onSubmit({
      name: values.name.trim(),
      type: values.type as PriceListItemTypesEnum,
      unit: isDataType ? null : (values.unit as PriceListItemUnitsEnum),
      unit_price: isDataType ? null : values.unit_price,
      vat_rate: values.vat_rate,
    });
  };

  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (validate && isFormValid.current) {
      setFormChanged(false);
      submitRef.current();
    }
  }, [validate, setFormChanged]);

  // RENDER
  return (
    <MainContent>
      <article className='sg-u-vs-4'>
        <PageTitle>
          <FormattedMessage {...priceListFormTitleMessages[formType]} />
        </PageTitle>
        {loadQueryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...priceListFormQueryMessages.queryStateWaiting} />
          </WaitingAlert>
        )}
        {loadQueryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...priceListFormQueryMessages.loadQueryStateFail} />
          </DangerAlert>
        )}
        {(loadQueryState === QUERY_STATE.SUCCESS || formType === PRICE_LIST_ITEM_FORM_TYPE.NEW) && (
          <Form onSubmit={handleSubmit}>
            <div className='sg-u-vs-2'>
              <FormBlock>
                <FormRow>
                  <FormItem {...name}>
                    <Input {...name} />
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem {...type}>
                    <Select hasEmptyItem {...type} />
                  </FormItem>
                  {!isDataType && (
                    <FormItem {...unit}>
                      <Select hasEmptyItem {...unit} />
                    </FormItem>
                  )}
                </FormRow>
                <FormRow>
                  {!isDataType && (
                    <FormItem {...unit_price}>
                      <Input type='number' min={0} step={0.01} {...unit_price} />
                      {resource.data?.unit_price_with_vat && (
                        <div className='sg-a-mt-1/2 sg-u-type-sml sg-u-c-body-darkest'>
                          <FormattedMessage
                            {...priceListFormMessages.unitPriceWithVatValue}
                            values={{ price: resource.data.unit_price_with_vat }}
                          />
                        </div>
                      )}
                    </FormItem>
                  )}
                  <FormItem {...vat_rate}>
                    <Input type='number' min={0} max={100} step={0.01} {...vat_rate} />
                  </FormItem>
                </FormRow>
                {formType === PRICE_LIST_ITEM_FORM_TYPE.EDIT && resource.data?.created && (
                  <FormRow>
                    <FormItem label={intl.formatMessage(priceListMessages.created)}>
                      <time dateTime={resource.data.created}>
                        <FormattedDate value={new Date(resource.data.created)} />{' '}
                        <FormattedTime value={new Date(resource.data.created)} />
                      </time>
                    </FormItem>
                  </FormRow>
                )}
              </FormBlock>

              {isDataType && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...priceListMessages.bandsTitle} />
                  </h3>
                  <div className='sg-u-box sg-a-p-2'>
                    <PriceBandsTable
                      bands={ownBands?.length ? ownBands : typeBands}
                      vatRate={vat_rate.value}
                      isReference={!ownBands?.length && !!typeBands}
                    />
                  </div>
                </section>
              )}

              {submitQueryState === QUERY_STATE.WAITING && (
                <WaitingAlert>
                  <FormattedMessage {...priceListFormQueryMessages.queryStateWaiting} />
                </WaitingAlert>
              )}
              {submitQueryState === QUERY_STATE.FAIL && (
                <DangerAlert>
                  <FormattedMessage {...priceListFormFailMessages[formType]} />
                </DangerAlert>
              )}
              {submitQueryState === QUERY_STATE.SUCCESS && (
                <SuccessAlert>
                  <FormattedMessage {...priceListFormQueryMessages.submitQueryStateSuccess} />
                </SuccessAlert>
              )}

              {deleteQueryState === QUERY_STATE.WAITING && (
                <WaitingAlert>
                  <FormattedMessage {...priceListFormQueryMessages.queryStateWaiting} />
                </WaitingAlert>
              )}
              {deleteQueryState === QUERY_STATE.FAIL && (
                <DangerAlert>
                  <FormattedMessage
                    {...(isDeleteBlockedByUse
                      ? priceListFormQueryMessages.deleteQueryStateFailInUse
                      : priceListFormQueryMessages.deleteQueryStateFail)}
                  />
                </DangerAlert>
              )}

              <BtnBar>
                <>
                  {formType === PRICE_LIST_ITEM_FORM_TYPE.EDIT && (
                    <>
                      {isFormChanged ? (
                        <>
                          <CtaBtn type='submit'>
                            <FormattedMessage {...priceListFormBtnMessages.save} />
                          </CtaBtn>
                          <SecondaryBtn isDanger onClick={handleCancel}>
                            <FormattedMessage {...priceListFormBtnMessages.cancel} />
                          </SecondaryBtn>
                        </>
                      ) : (
                        <CtaBtn onClick={handleClose}>
                          <FormattedMessage {...priceListFormBtnMessages.undo} />
                        </CtaBtn>
                      )}
                      <SecondaryBtn isDanger onClick={handleDelete}>
                        <FormattedMessage {...priceListFormBtnMessages.delete} />
                      </SecondaryBtn>
                    </>
                  )}
                  {formType === PRICE_LIST_ITEM_FORM_TYPE.NEW && (
                    <>
                      <CtaBtn type='submit'>
                        <FormattedMessage {...priceListFormBtnMessages.create} />
                      </CtaBtn>
                      <SecondaryBtn isDanger onClick={handleClear}>
                        <FormattedMessage {...priceListFormBtnMessages.clear} />
                      </SecondaryBtn>
                    </>
                  )}
                </>
              </BtnBar>
            </div>
          </Form>
        )}
      </article>
    </MainContent>
  );
};
